/**
 * DraftListPage — admin index of outreach drafts.
 */

import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { DashboardNavbar } from '@/components/dashboard/DashboardNavbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { listDrafts } from '@/services/draftService';
import { FileText, Loader2, Plus } from 'lucide-react';
import type { FiggyDraft } from '@/types/draft';

export default function DraftListPage() {
  const { profile } = useAuth();
  const [drafts, setDrafts] = useState<FiggyDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (profile?.role !== 'admin') return;
    (async () => {
      try {
        setDrafts(await listDrafts());
      } catch (err: any) {
        setError(err.message || 'Failed to load drafts');
      } finally {
        setLoading(false);
      }
    })();
  }, [profile?.role]);

  if (profile?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center justify-between mb-6 gap-4">
            <div>
              <div className="flex items-center gap-2">
                <FileText className="h-6 w-6 text-figgy-600" />
                <h1 className="text-2xl font-semibold">Draft Builder</h1>
              </div>
              <p className="text-muted-foreground mt-1">
                Premium Figgy preview pages for cold outreach.
              </p>
            </div>
            <Button asChild>
              <Link to="/admin/drafts/new">
                <Plus className="h-4 w-4 mr-1.5" />
                New Draft
              </Link>
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : drafts.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground">
              No drafts yet. Create one to start a preview for a lead.
            </Card>
          ) : (
            <div className="space-y-3">
              {drafts.map((d) => {
                const title = d.business_name || d.provider_name || d.instagram_handle || 'Untitled draft';
                return (
                  <Card key={d.id} className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="min-w-0">
                        <Link
                          to={`/admin/drafts/${d.id}`}
                          className="font-medium hover:underline truncate block"
                        >
                          {title}
                        </Link>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {d.status}
                          {d.category ? ` · ${d.category}` : ''}
                          {d.city ? ` · ${d.city}` : ''}
                        </p>
                      </div>
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/admin/drafts/${d.id}`}>Edit</Link>
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
